import 'twin.macro';
import 'styled-components/macro';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { ButtonV2 } from '../../../component-lib/atoms/button-v2/ButtonV2';
import {
  Table,
  TableBody,
  TableCell,
  TableChevron,
  TableContainer,
  TableHeader,
  TableRow,
} from '../../../component-lib/molecules/table/Table';
import { datetimeFormat } from '../../../support/dates';
import { CartItem, ShoppingCartContextType } from '../../context/ShoppingCart';

export interface CartTableProps {
  items: CartItem[];
  removeFromCart: ShoppingCartContextType['removeFromCart'];
}

const columnHelper = createColumnHelper<CartItem>();

export function CartTable({ items, removeFromCart }: CartTableProps) {
  const table = useReactTable({
    columns: useMemo(
      () => [
        columnHelper.accessor(row => row.item.title, {
          cell: ({ getValue, row }) => (
            <Link tw="display-5 text-primary block" to={`/toy-shop/sweet/${row.original.item.id}`}>
              {getValue()}
            </Link>
          ),
          header: 'Title',
          id: 'title',
        }),
        columnHelper.accessor(row => datetimeFormat(new Date(row.item.expiresAt)), {
          cell: ({ getValue }) => <div tw="display-5 text-primary">{getValue()}</div>,
          header: 'Expires at',
          id: 'expiresAt',
        }),
        columnHelper.accessor('quantity', {
          cell: ({ getValue }) => <div tw="display-5 text-primary">{getValue()}</div>,
          header: 'Quantity',
          id: 'quantity',
        }),
        columnHelper.display({
          cell: ({ row }) => <ButtonV2 onClick={() => removeFromCart(row.original.item.id)}>Delete</ButtonV2>,
          meta: {
            align: 'right',
          },
          id: 'delete',
        }),
      ],
      [removeFromCart],
    ),
    data: useMemo(() => items, [items]),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortDescFirst: false,
  });

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableCell
                  key={header.id}
                  as="th"
                  $align={header.column.columnDef.meta?.align}
                  canSort={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && <TableChevron $sortDirection={header.column.getIsSorted()} />}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map(cell => (
                <TableCell $align={cell.column.columnDef.meta?.align} as="td" key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
